import { Href, Location, Query } from 'redux-little-router';

import {
    CampaignCriteriaKey,
    CampaignCriteriaSetTitles,
    GroupCriteriaKey,
    GroupCriteriaSetTitles
} from '../declarations/filter/filter';
import { StatKey } from '../declarations/stats/stats';
import { expressionToString } from '../mappers/filter/filter';
import { campaigns, clients, GRID_PREFIX, groups } from '../typings/common';
import { CampaignsCalcTypeFilter } from '../typings/domain';
import { FilterExpressionValueType, Operator } from '../typings/filter';
import {
    GridContextType,
    GridContextUrlParams,
    GridScreenUrlQueryParams,
    GridScreenUrlRouteParams,
    GridScreenUrlSearchQueryParams,
    GridSourceParams,
    GridViewType,
    SortParams
} from '../typings/grid';

import { UrlHelpers, UrlSearchParams } from './UrlHelpers';

export class GridScreenLinkBuilder {
    static getScreenPathName(): string {
        return '/registered/main.pl/';
    }

    /**
     * Возвращает url с установленным набором параметров, переданных в params
     */
    static getUrl(
        queryParams: GridScreenUrlQueryParams,
        routeParams: GridScreenUrlRouteParams,
        pathname: string,
        isPure?: boolean
    ): Location {
        const search: UrlSearchParams = Object.keys(queryParams).reduce(
            (res, name) => {
                const paramName = GridScreenUrlSearchQueryParams[name];

                if (!paramName) {
                    throw new Error("Couldn't set ${param} param");
                }

                res[paramName] = queryParams[name];

                return res;
            },
            {}
        );

        return {
            pathname: isPure
                ? pathname
                : UrlHelpers.createNewDirectUrlPathByParams(
                      GRID_PREFIX,
                      routeParams.view
                  ),
            query: isPure
                ? undefined
                : UrlHelpers.getRouteQueryByParams(search),
            search: UrlHelpers.getSearchStringByParams(search)
        };
    }

    /**
     * Возвращает URL для установки сортировки по заданному ключу и направлению
     */
    static getSortLinkUrl(
        pathname: string,
        { direction, key, goalId }: SortParams
    ): Href {
        const baseQueryParams = {
            sortDirection: direction,
            sortKey: key,
            sortGoalId: goalId ? goalId : null
        };

        return GridScreenLinkBuilder.getUrl(
            baseQueryParams,
            {},
            pathname,
            true
        );
    }

    static getContextTypeUrl(
        context: GridContextType,
        view: GridViewType,
        params: GridContextUrlParams,
        pathname: string
    ): Location {
        const {
            login,
            contextIds,
            campaignsIds,
            filter,
            statPreset,
            statFrom,
            statTo,
            campaignsCalcTypeFilter
        } = params;

        switch (context) {
            case clients:
                return GridScreenLinkBuilder.getUrl(
                    {
                        filter: filter,
                        ulogin: login,
                        statPreset,
                        statFrom,
                        statTo
                    },
                    {
                        view
                    },
                    pathname
                );

            case campaigns:
                return GridScreenLinkBuilder.getUrl(
                    {
                        campaignsIds: contextIds,
                        filter: filter,
                        ulogin: login,
                        statPreset,
                        statFrom,
                        statTo
                    },
                    {
                        view
                    },
                    pathname
                );

            case groups:
                return GridScreenLinkBuilder.getUrl(
                    {
                        groupsIds: contextIds,
                        campaignsIds: campaignsIds,
                        filter: filter,
                        ulogin: login,
                        calcType: campaignsCalcTypeFilter,
                        statPreset,
                        statFrom,
                        statTo
                    },
                    {
                        view
                    },
                    pathname
                );

            default:
                return GridScreenLinkBuilder.getUrl(
                    {
                        ulogin: login,
                        statPreset,
                        statFrom,
                        statTo
                    },
                    {
                        view
                    },
                    pathname
                );
        }
    }

    static getContextTypeQueryRelevantParams(
        context: GridContextType,
        view: GridViewType,
        params: GridContextUrlParams,
        sourceParams: GridSourceParams
    ): Query | undefined {
        const { sortParams } = sourceParams;

        const query = GridScreenLinkBuilder.getContextTypeUrl(
            context,
            view,
            params,
            sourceParams.pathname
        ).query;

        if (sortParams) {
            const relevantSortParams = GridScreenLinkBuilder.getRelevantSortParams(
                sortParams
            );

            if (query && relevantSortParams === undefined) {
                delete query['sort-key'];
                delete query['sort-direction'];
            }
        }

        return query;
    }

    static getContextTypeRelevantUrl(
        context: GridContextType,
        view: GridViewType,
        params: GridContextUrlParams,
        sourceParams: GridSourceParams
    ): Href {
        const query = GridScreenLinkBuilder.getContextTypeQueryRelevantParams(
            context,
            view,
            params,
            sourceParams
        );

        return {
            pathname: UrlHelpers.createNewDirectUrlPathByParams(
                GRID_PREFIX,
                view
            ),
            query
        };
    }

    static getRelevantSortParams(
        sortParams?: SortParams
    ): SortParams | undefined {
        if (sortParams && StatKey[sortParams.key]) {
            return sortParams;
        }

        return undefined;
    }

    static getGridLink(
        queryParams: UrlSearchParams,
        routeParams: GridScreenUrlRouteParams
    ): Location {
        const location = GridScreenLinkBuilder.getUrl(
            queryParams,
            routeParams,
            GridScreenLinkBuilder.getScreenPathName()
        );

        return location;
    }

    static getCampaignLinkById(campaignId: string, ulogin: string): Location {
        return this.getGridLink(
            {
                ulogin,
                filter: expressionToString({
                    field: CampaignCriteriaSetTitles[CampaignCriteriaKey.id],
                    operator: Operator.EQUALS,
                    value: {
                        type: FilterExpressionValueType.text,
                        value: campaignId
                    }
                })
            },
            {
                view: campaigns
            }
        );
    }

    static getUrlShowMinusPhrasesBindedGroups(
        minusPhrasePackId: string,
        campaignsIds: string[],
        login: string
    ): Location {
        return GridScreenLinkBuilder.getGridLink(
            {
                campaignsIds: campaignsIds.join(','),
                calcType: CampaignsCalcTypeFilter.CPC,
                filter: expressionToString({
                    field:
                        GroupCriteriaSetTitles[
                            GroupCriteriaKey.libraryMinusPhrasePacks
                        ],
                    operator: Operator.EQUALS,
                    value: {
                        type: FilterExpressionValueType.numberSet,
                        value: [minusPhrasePackId]
                    }
                }),
                ulogin: login
            },
            {
                view: groups
            }
        );
    }
}
